import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  applyEdgeChanges,
  applyNodeChanges,
  Node,
  Edge,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";
import { IEmployeeInOrgchartData } from "./api";

type State = {
  allNodes: Node[];
  nodes: Node[];
  edges: Edge[];
  nodeToFocusOn?: { id: string; isCollapsed: boolean };
  searchedNodeId: string | null;
  wasSearchTriggered: boolean;
  visibleNodesRef: Map<string, boolean>;
  childrenCollapsedStatus: Map<string, boolean>;
  wasSearchedNodeCleared: boolean;
  paginationCursors: Map<string, string>;
  hierarchyData: IEmployeeInOrgchartData | null;
  rootSubordinatesCount: number | null;
};

type Action = {
  setAllNodes: (nodes: State["allNodes"]) => void;
  setNodes: (nodes: State["nodes"]) => void;
  setEdges: (edges: State["edges"]) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  setNodeToFocusOn: (nodesToFocusOn: State["nodeToFocusOn"]) => void;
  setSearchedNodeId: (searchedNodeId: State["searchedNodeId"]) => void;
  setWasSearchTriggered: (wasSearchTriggered: State["wasSearchTriggered"]) => void;
  setVisibleNodesRef: (visibleNodesRef: State["visibleNodesRef"]) => void;
  setChildrenCollapsedStatus: (
    childrenCollapsedStatus: State["childrenCollapsedStatus"]
  ) => void;
  setWasSearchedNodeCleared: (
    wasSearchedNodeCleared: State["wasSearchedNodeCleared"]
  ) => void;
  updateVisibleNodeRef: (nodeId: string, isVisible: boolean) => void;
  toggleChildrenCollapsedStatus: (nodeId: string) => void;
  setPaginationCursor: (supervisorId: string, cursor: string) => void;
  setHierarchyData: (hierarchyData: State["hierarchyData"]) => void;
};

const useOrgchartEmployeesStore = create<State & Action>()(
  devtools((set, getState) => ({
    allNodes: [],
    nodes: [],
    edges: [],
    searchedNodeId: null,
    wasSearchTriggered: false,
    visibleNodesRef: new Map<string, boolean>(),
    childrenCollapsedStatus: new Map<string, boolean>(),
    wasSearchedNodeCleared: false,
    paginationCursors: new Map<string, string>(),
    hierarchyData: null,
    rootSubordinatesCount: null,
    nodeToFocusOn: undefined,
    setAllNodes: (allNodes) => set({ allNodes }),
    onNodesChange: (changes) =>
      set({ nodes: applyNodeChanges(changes, getState().nodes) }),
    onEdgesChange: (changes) =>
      set({ edges: applyEdgeChanges(changes, getState().edges) }),
    setNodes: (nodes) => set({ nodes }),
    setEdges: (edges) => set(() => ({ edges })),
    setNodeToFocusOn: (nodeToFocusOn) => set({ nodeToFocusOn }),
    setSearchedNodeId: (searchedNodeId) => set({ searchedNodeId }),
    setWasSearchTriggered: (wasSearchTriggered) => set({ wasSearchTriggered }),
    setVisibleNodesRef: (visibleNodesRef) => set({ visibleNodesRef }),
    setChildrenCollapsedStatus: (childrenCollapsedStatus) =>
      set({ childrenCollapsedStatus }),
    setWasSearchedNodeCleared: (wasSearchedNodeCleared) =>
      set({ wasSearchedNodeCleared }),
    updateVisibleNodeRef: (nodeId, isVisible) =>
      set((state) => ({
        visibleNodesRef: new Map(state.visibleNodesRef).set(nodeId, isVisible),
      })),
    toggleChildrenCollapsedStatus: (nodeId) =>
      set((state) => ({
        childrenCollapsedStatus: new Map(state.childrenCollapsedStatus).set(
          nodeId,
          !state.childrenCollapsedStatus.get(nodeId)
        ),
      })),
    setPaginationCursor: (supervisorId, cursor) =>
      set((state) => ({
        paginationCursors: new Map(state.paginationCursors).set(
          supervisorId,
          cursor
        ),
      })),
    setHierarchyData: (hierarchyData) => set({ hierarchyData }),
  }))
);

export default useOrgchartEmployeesStore;
